import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPermissionSets, deletePermissionSet } from '../api';
import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Spin,
  Pagination,
  Row,
  Col,
  Grid,
  Card,
  Tooltip,
  message,
  theme as antdTheme,
  Popconfirm,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { usePermissions } from '../../../hooks/usePermissions';
import { PermissionButton, ModuleType, ActionType } from 'components';
import 'bootstrap/dist/css/bootstrap.min.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PermissionSetListPage = () => {
  const [permissionSets, setPermissionSets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();
  const { token } = antdTheme.useToken();
  const { canViewModule, canViewCreateModule } = usePermissions();
  const module = ModuleType.PERMISSION;
  const screens = useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    loadPermissionSets();
  }, [pagination.current, pagination.pageSize, searchQuery]);

  const loadPermissionSets = async () => {
    setLoading(true);
    try {
      const response = await fetchPermissionSets({
        search: searchQuery,
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setPermissionSets(response.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.meta?.total || response.pagination?.total || response.data?.length || 0,
      }));
    } catch (err) {
      console.error('Failed to fetch permission sets', err);
      messageApi.error('Failed to load permission sets');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSearchQuery(searchTerm.trim());
  };

  const handleTableChange = (page, pageSize) => {
    setPagination({ ...pagination, current: page, pageSize });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deletePermissionSet(id);
      messageApi.success('Permission set deleted successfully');
      loadPermissionSets();
    } catch (err) {
      console.error('Failed to delete permission set', err);
      messageApi.error(err.response?.data?.message || 'Failed to delete permission set');
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
    {
      title: 'Subject Type',
      dataIndex: 'type',
      key: 'type',
      render: (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : '-',
      responsive: ['sm', 'md', 'lg'],
    },
    {
      title: 'Associated Subject ID',
      dataIndex: 'subject',
      key: 'subject',
      responsive: ['md', 'lg'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle" onClick={(e) => e.stopPropagation()}>
          <Button type="link" onClick={() => navigate(`/permission-sets/${record._id}`)}>
            View / Edit
          </Button>
          {!record.isSystem && (
            <Popconfirm
              title="Delete the permission set"
              description="Are you sure you want to delete this permission set? It will be removed from all assigned users."
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
  ];

  const renderMobileCard = (ps) => (
    <Card
      key={ps._id}
      size="small"
      hoverable
      style={{ marginBottom: 12 }}
      onClick={() => navigate(`/permission-sets/${ps._id}`)}
    >
      <Row justify="space-between" align="middle">
        <Col span={16}>
          <Title level={5} style={{ marginBottom: 0 }}>{ps.name || '-'}</Title>
          <Text type="secondary" style={{ display: 'block' }}>{ps.type ? ps.type.charAt(0).toUpperCase() + ps.type.slice(1) : '-'}</Text>
        </Col>
        <Col span={8} style={{ textAlign: 'right' }}>
          <Text type="secondary">Subject: {ps.subject || '-'}</Text>
        </Col>
      </Row>
      <div style={{ marginTop: 8, textAlign: 'right' }}>
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View or Edit">
            <Button size="small" type="link" onClick={() => navigate(`/permission-sets/${ps._id}`)}>
              View / Edit
            </Button>
          </Tooltip>
          {!ps.isSystem && (
            <Popconfirm
              title="Delete the permission set"
              description="Are you sure you want to delete this permission set? It will be removed from all assigned users."
              onConfirm={() => handleDelete(ps._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button size="small" type="link" danger>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>
    </Card>
  );

   if (!canViewModule(module)) {
    return (
      <div style={{ textAlign: 'center', marginTop: 60 }}>
        <Text type="danger">You do not have permission to view permission set.</Text>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card>
            {contextHolder}
            <Row justify="space-between" align="middle" className="mb-4">
              <Col>
                <Title level={2} style={{ margin: 0 }}>Permission Sets</Title>
              </Col>
              <Col>
                {canViewCreateModule(module) && (
                  <PermissionButton
                    type="primary"
                    buttonType={ActionType.CREATE}
                    module={module}
                    onClick={() => navigate('/permission-sets/create')}
                    size={screens.xs ? 'small' : 'middle'}
                  >
                    Create Permission Set
                  </PermissionButton>
                )}
              </Col>
            </Row>

            {/* Search Row */}
            <Row className="mb-3">
              <Col xs={24} sm={18} md={20}>
                <Input.Search
                  placeholder="Search by name"
                  allowClear
                  enterButton={<SearchOutlined />}
                  size={screens.xs ? 'small' : 'middle'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onSearch={handleSearch}
                />
              </Col>
            </Row>

            <Spin spinning={loading}>
              {screens.md ? (
                <Table
                  columns={columns}
                  dataSource={permissionSets}
                  rowKey={(record) => record._id}
                  pagination={false}
                  onRow={(record) => ({
                    onClick: () => navigate(`/permission-sets/${record._id}`),
                    style: { cursor: 'pointer' },
                  })}
                  locale={{ emptyText: 'No permission sets found.' }}
                  scroll={{ x: 'max-content' }}
                  size={screens.lg ? 'middle' : 'small'}
                />
              ) : (
                <div>
                  {permissionSets && permissionSets.length ? permissionSets.map(renderMobileCard) : (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                      <Text type="secondary">No permission sets found.</Text>
                    </div>
                  )}
                </div>
              )}

              {pagination.total > 0 && (
                <Row justify="center" className="mt-4">
                  <Col>
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={pagination.total}
                      onChange={handleTableChange}
                      showSizeChanger
                      showQuickJumper={!screens.xs}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} permission sets`}
                      size={screens.xs ? 'small' : 'default'}
                      responsive
                    />
                  </Col>
                </Row>
              )}
            </Spin>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PermissionSetListPage;
